import React, { useRef, useCallback, useEffect, useMemo } from "react";
import { createButtonPressAnimation, attemptFullscreenToggle } from "../../../../utilities";
import { Sprite, Container, Text, Graphics } from "@pixi/react";
import * as PIXI from "pixi.js";
import anime from "animejs";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { openBonusBuy, closeMenu } from "../../../../State/actions";
import {
  selectMenuActive,
  selectCriticalAnimationPlaying,
  selectCounterAnimationFinished,
  selectIsSpinning,
  selectAutoPlayActive,
  selectFreeRoundsActive,
  selectHoldAndWinActive,
} from "../../../../State/selectors";
import { SOUND_NAME } from "../../../../sounds";
import { ASSET_NAME } from "../../../../assets";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import { PixiSettings } from "../../../../pixiSettings";
import { soundManager } from "../../../../SoundManager";
import { FONT_CONFIG } from "../../../../textureConfigs";
import { TextStyle, TextMetrics } from "pixi.js";
import { FEATURES_CONFIG } from "../../../../featuresConfig";

// Bonus buy button configuration - matches UI button dimensions
const BONUS_BUY_BUTTON_CONFIG = {
  BASE_WIDTH: 80,
  BASE_HEIGHT: 80,
  DEVICE_CONFIGS: {
    [PixiSettings.DEVICE.MOBILE]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.07,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.85,
      },
    },
    [PixiSettings.DEVICE.TABLET]: {
      [PixiSettings.ORIENTATION.PORTRAIT]: {
        deviceMultiplier: 2.4,
      },
      [PixiSettings.ORIENTATION.LANDSCAPE]: {
        deviceMultiplier: 1.6,
      },
    },
    [PixiSettings.DEVICE.DESKTOP]: {
      deviceMultiplier: 1.1,
    },
  },
};

const ButtonBonusBuy = () => {
  // eslint-disable-next-line no-unused-vars
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { getResponsiveConfig, device, orientation, dimensions } =
    useDimensions();
  const menuActive = useSelector(selectMenuActive);
  // Guards - prevent starting new spin if ongoing spin, win lines displaying, critical animation, or autoplay active
  const criticalAnimationPlaying = useSelector(selectCriticalAnimationPlaying);
  const isSpinning = useSelector(selectIsSpinning);
  const counterAnimationFinished = useSelector(selectCounterAnimationFinished);
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const freeRoundsActive = useSelector(selectFreeRoundsActive);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);
  const spinComplete = useSelector((state) => state.spin.spinComplete);
  const currentRound = useSelector((state) => state.game.currentRound);

  // Add ref for the sprite and text to animate
  const buttonSpriteRef = useRef(null);
  const buttonTextRef = useRef(null);
  const animationRef = useRef(null);
  const restoreTimeoutRef = useRef(null);

  // Track if press animation is currently running (prevents rapid click shrinking)
  const isPressAnimatingRef = useRef(false);

  // Refs to track current dimensions for animation (no re-renders)
  const dimensionsRef = useRef({ width: 0, height: 0 });
  const isInitializedRef = useRef(false);

  // Check if we're on mobile landscape (only apply animation here) - memoized
  const isMobileLandscape = useMemo(
    () =>
      device === PixiSettings.DEVICE.MOBILE &&
      orientation === PixiSettings.ORIENTATION.LANDSCAPE,
    [device, orientation]
  );

  // Create brightness filter for hover effect
  const brightnessFilter = useMemo(() => {
    const filter = new PIXI.filters.ColorMatrixFilter();
    filter.brightness(1.3, false); // 30% brighter (1.3 = 130% of original)
    filter.resolution = window.devicePixelRatio || 2; // Match device resolution
    filter.padding = 10; // Prevent edge clipping
    return filter;
  }, []);

  // Use ref for hover state to avoid unnecessary re-renders
  const isHoveredRef = useRef(false);

  // Add ref to track if button is currently processing a click
  const isProcessingClickRef = useRef(false);

  // Add ref to store timeout ID for proper cleanup
  const debounceTimeoutRef = useRef(null);

  // Track previous menu state to detect actual state changes (not initial render)
  const previousMenuStateRef = useRef(null);

  // Ref to track if bonus buy was triggered (set immediately on click, before API response)
  const bonusBuyTriggeredRef = useRef(false);

  // Use active background when bonus buy was just triggered OR Hold & Win is active (NOT free rounds)
  const isButtonActive = bonusBuyTriggeredRef.current || holdAndWinActive;
  const texture = PIXI.Assets.get(
    isButtonActive
      ? ASSET_NAME.button_buy_bonus_background_active
      : ASSET_NAME.popup_radio_button_active
  );

  // Get responsive configuration from context - memoized to prevent recalculation
  const buttonConfig = useMemo(
    () => getResponsiveConfig(BONUS_BUY_BUTTON_CONFIG, true),
    [getResponsiveConfig]
  );

  // Get translation text - show "ACTIVE" when button is active (bonus buy triggered or Hold & Win), otherwise "Featured Spins" - memoized
  const displayText = useMemo(
    () =>
      isButtonActive
        ? t("common:active").toUpperCase()
        : t("buy_bonus").toUpperCase(),
    [isButtonActive, t]
  );

  // Calculate ideal font size based on button size
  const idealFontSize = useMemo(() => {
    const buttonSize = Math.min(buttonConfig.width, buttonConfig.height);
    return Math.round(buttonSize * 0.22);
  }, [buttonConfig.width, buttonConfig.height]);

  // Calculate max width for text (with padding)
  const maxTextWidth = useMemo(() => {
    return buttonConfig.width * 0.8; // 80% of button width for padding
  }, [buttonConfig.width]);

  // Auto-scaling font size calculation - similar to BalanceDisplay
  const finalFontSize = useMemo(() => {
    // Create temp style to measure text
    const tempStyle = new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: idealFontSize,
      fontWeight: "600",
      align: "center",
      wordWrap: true,
      wordWrapWidth: maxTextWidth,
      breakWords: false,
    });

    // Measure actual text dimensions
    const metrics = TextMetrics.measureText(displayText, tempStyle);

    // If text exceeds max width, scale down proportionally
    if (metrics.width > maxTextWidth) {
      const scale = maxTextWidth / metrics.width;
      return Math.round(idealFontSize * scale);
    }

    // Return ideal font size if it fits
    return idealFontSize;
  }, [displayText, idealFontSize, maxTextWidth]);

  // Text style - white color when active (bonus buy triggered or Hold & Win), black otherwise
  const textStyle = useMemo(() => {
    return new TextStyle({
      fontFamily: FONT_CONFIG.PRIMARY,
      fontSize: finalFontSize,
      fontWeight: "700", // Changed from "600" to "700" for bolder text
      fill: isButtonActive ? 0xffffff : 0x000000, // White when active, black otherwise
      align: "center",
      wordWrap: true,
      wordWrapWidth: maxTextWidth,
      breakWords: false,
    });
  }, [finalFontSize, maxTextWidth, isButtonActive]);

  // Calculate responsive button position based on device and orientation - memoized
  // Uses toolbar width instead of full screen width
  const buttonPosition = useMemo(() => {
    if (device === PixiSettings.DEVICE.MOBILE) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: dimensions.width - buttonConfig.width / 1.6,
          y: -dimensions.height / 5.2 - buttonConfig.height / 1.2,
        };
      } else {
        // landscape
        return {
          x: buttonConfig.width * 0.7,
          y: -buttonConfig.height * 0.7,
        };
      }
    } else if (device === PixiSettings.DEVICE.TABLET) {
      if (orientation === PixiSettings.ORIENTATION.PORTRAIT) {
        return {
          x: dimensions.width - buttonConfig.width * 0.86,
          y: -dimensions.height / 3.35 + buttonConfig.height / 2,
        };
      } else {
        return {
          x: buttonConfig.width * 1.4,
          y: -buttonConfig.height * 1.2,
        };
      }
    } else {
      return {
        x: buttonConfig.width * 2.3,
        y: -buttonConfig.height * 1,
      };
    }
  }, [
    device,
    orientation,
    dimensions.width,
    dimensions.height,
    buttonConfig.width,
    buttonConfig.height,
  ]);

  // Calculate if button should be disabled (guard conditions) - memoized
  // Button is disabled during spins, animations, autoplay, free rounds, or hold and win
  const isDisabled = useMemo(() => {
    const hasWin = currentRound?.win && currentRound.win > 0;
    const winNotFullyDisplayed =
      hasWin && spinComplete && !counterAnimationFinished;
    return (
      isSpinning ||
      criticalAnimationPlaying ||
      winNotFullyDisplayed ||
      autoPlayActive ||
      (FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive) ||
      holdAndWinActive
    );
  }, [
    currentRound?.win,
    spinComplete,
    counterAnimationFinished,
    isSpinning,
    criticalAnimationPlaying,
    autoPlayActive,
    freeRoundsActive,
    holdAndWinActive,
  ]);

  const handleClick = () => {
    // Close menu if open
    dispatch(closeMenu());

    // Attempt to toggle fullscreen if conditions are met
    attemptFullscreenToggle(dispatch, device);

    // Prevent starting new spin if ongoing spin, win lines displaying, or critical animation
    if (isDisabled) {
      return;
    }

    // Prevent rapid clicks if already processing
    if (isProcessingClickRef.current) {
      return;
    }

    // Clear any existing timeout before starting new one
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    isProcessingClickRef.current = true;

    // soundManager.play(SOUND_NAME.button_click);
    soundManager.play(SOUND_NAME.buy_bonus);

    // Open the bonus buy popup
    dispatch(openBonusBuy());

    // Add press animation
    if (buttonSpriteRef.current && !isPressAnimatingRef.current) {
      isPressAnimatingRef.current = true;
      createButtonPressAnimation(buttonSpriteRef.current, {
        targetWidth: buttonConfig.width,
        targetHeight: buttonConfig.height,
        onComplete: () => {
          isPressAnimatingRef.current = false;
        }
      });
    }

    // Reset processing flag after animation completes (500ms debounce)
    debounceTimeoutRef.current = setTimeout(() => {
      isProcessingClickRef.current = false;
    }, 500);
  };

  // Hover effect handlers - using refs for direct sprite manipulation
  const handlePointerOver = useCallback(() => {
    // Don't apply hover effect if button is disabled
    if (criticalAnimationPlaying) {
      return;
    }
    if (buttonSpriteRef.current) {
      isHoveredRef.current = true;
      // Directly apply brightness filter to sprite
      if (
        !buttonSpriteRef.current.filters ||
        buttonSpriteRef.current.filters.length === 0
      ) {
        buttonSpriteRef.current.filters = [brightnessFilter];
      }
    }
  }, [buttonSpriteRef, brightnessFilter, criticalAnimationPlaying]);

  const handlePointerOut = useCallback(() => {
    if (buttonSpriteRef.current) {
      isHoveredRef.current = false;
      // Directly remove brightness filter from sprite
      buttonSpriteRef.current.filters = [];
    }
  }, [buttonSpriteRef]);

  // Initialize sprite dimensions on mount (only for mobile landscape)
  useEffect(() => {
    if (
      buttonSpriteRef.current &&
      !isInitializedRef.current &&
      isMobileLandscape
    ) {
      // Start with full size - set BOTH ref and actual sprite dimensions
      dimensionsRef.current.width = buttonConfig.width;
      dimensionsRef.current.height = buttonConfig.height;
      // CRITICAL: Also set the actual sprite dimensions immediately
      buttonSpriteRef.current.width = buttonConfig.width;
      buttonSpriteRef.current.height = buttonConfig.height;
      isInitializedRef.current = true;
    } else if (!isMobileLandscape) {
      // For non-mobile-landscape, ensure button stays visible
      isInitializedRef.current = true;
    }
  }, [isMobileLandscape, buttonConfig.width, buttonConfig.height]);

  // Width and height animation effect based on menuActive state (only on mobile landscape)
  useEffect(() => {
    // Only apply animation on mobile landscape
    if (!isMobileLandscape) return;
    if (!buttonSpriteRef.current || !isInitializedRef.current) return;

    // Stop any existing animation
    if (animationRef.current) {
      anime.remove(animationRef.current);
    }

    // Clear any existing restore timeout when state changes
    if (restoreTimeoutRef.current) {
      clearTimeout(restoreTimeoutRef.current);
      restoreTimeoutRef.current = null;
    }

    // Check if this is initial render before any state changes
    const isInitialRender = previousMenuStateRef.current === null;

    if (isInitialRender) {
      // Skip animation on initial render - let Sprite props handle dimensions
      previousMenuStateRef.current = menuActive;
      return;
    }

    if (menuActive) {
      // When menu opens, immediately shrink button (scale to 0)
      // Get current dimensions from sprite (preserve current state)
      const currentWidth =
        buttonSpriteRef.current.width !== undefined
          ? buttonSpriteRef.current.width
          : buttonConfig.width;
      const currentHeight =
        buttonSpriteRef.current.height !== undefined
          ? buttonSpriteRef.current.height
          : buttonConfig.height;

      // Update ref with current values
      dimensionsRef.current.width = currentWidth;
      dimensionsRef.current.height = currentHeight;

      // Update previous state for next render
      previousMenuStateRef.current = menuActive;

      animationRef.current = anime({
        targets: dimensionsRef.current,
        width: 0,
        height: 0,
        duration: 150,
        delay: 0,
        easing: "easeInQuad",
        update: () => {
          // Directly update sprite dimensions without causing re-renders
          if (buttonSpriteRef.current) {
            buttonSpriteRef.current.width = dimensionsRef.current.width;
            buttonSpriteRef.current.height = dimensionsRef.current.height;
          }
          // Fade out text as button shrinks
          if (buttonTextRef.current) {
            const progress = dimensionsRef.current.width / buttonConfig.width;
            buttonTextRef.current.alpha = progress;
          }
        },
      });
    } else {
      // FIXED: Only animate to 0 if menu was actually open before (not initial render)
      // Check if previous state was true (menu was open)
      const wasMenuPreviouslyActive = previousMenuStateRef.current === true;
      // Update previous state for next render
      previousMenuStateRef.current = menuActive;

      if (wasMenuPreviouslyActive) {
        // When menu closes, ensure button stays at 0 until timeout fires
        // Force dimensions to 0 to prevent immediate restore
        dimensionsRef.current.width = 0;
        dimensionsRef.current.height = 0;
        if (buttonSpriteRef.current) {
          buttonSpriteRef.current.width = 0;
          buttonSpriteRef.current.height = 0;
        }

        // Wait for menu closing animation to complete before restoring button
        restoreTimeoutRef.current = setTimeout(() => {
          // Update ref with 0 as starting point before animating
          dimensionsRef.current.width = 0;
          dimensionsRef.current.height = 0;

          animationRef.current = anime({
            targets: dimensionsRef.current,
            width: buttonConfig.width,
            height: buttonConfig.height,
            duration: 150,
            delay: 0,
            easing: "easeOutQuad",
            update: () => {
              // Directly update sprite dimensions without causing re-renders
              if (buttonSpriteRef.current) {
                buttonSpriteRef.current.width = dimensionsRef.current.width;
                buttonSpriteRef.current.height = dimensionsRef.current.height;
              }
              // Fade in text as button grows
              if (buttonTextRef.current) {
                const progress =
                  dimensionsRef.current.width / buttonConfig.width;
                buttonTextRef.current.alpha = progress;
              }
            },
          });
        }, 300); // Wait for menu closing animation
      }
      // else: Initial load with menuActive=false, let Sprite props handle dimensions
    }

    // Cleanup function to stop animation and timeout on unmount
    return () => {
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
        restoreTimeoutRef.current = null;
      }
      if (animationRef.current) {
        anime.remove(animationRef.current);
      }
    };
  }, [menuActive, isMobileLandscape, buttonConfig.width, buttonConfig.height]);

  // Remove hover effects when button becomes disabled
  useEffect(() => {
    if (criticalAnimationPlaying && buttonSpriteRef.current) {
      buttonSpriteRef.current.filters = [];
      isHoveredRef.current = false;
    }
  }, [criticalAnimationPlaying]);

  // Listen for bonus buy triggered event to set active state immediately
  useEffect(() => {
    const handleBonusBuyTriggered = () => {
      bonusBuyTriggeredRef.current = true;
      // Force re-render to update texture and text
      if (buttonSpriteRef.current) {
        buttonSpriteRef.current.texture = PIXI.Assets.get(ASSET_NAME.button_buy_bonus_background_active);
      }
      if (buttonTextRef.current) {
        buttonTextRef.current.text = t("common:active").toUpperCase();
        buttonTextRef.current.style = new TextStyle({
          fontFamily: FONT_CONFIG.PRIMARY,
          fontSize: finalFontSize,
          fontWeight: "700",
          fill: 0xffffff, // White when active
          align: "center",
          wordWrap: true,
          wordWrapWidth: maxTextWidth,
          breakWords: false,
        });
      }
    };

    window.addEventListener('bonusBuyTriggered', handleBonusBuyTriggered);

    return () => {
      window.removeEventListener('bonusBuyTriggered', handleBonusBuyTriggered);
    };
  }, [t, finalFontSize, maxTextWidth]);

  // Clear bonusBuyTriggeredRef when free rounds or hold and win actually become active
  useEffect(() => {
    if (((FEATURES_CONFIG.FREE_ROUNDS.ENABLED && freeRoundsActive) || holdAndWinActive) && bonusBuyTriggeredRef.current) {
      bonusBuyTriggeredRef.current = false;
    }
  }, [freeRoundsActive, holdAndWinActive]);

  // Clean up hover effects and debounce timeout on unmount
  useEffect(() => {
    const sprite = buttonSpriteRef.current;
    return () => {
      if (sprite) {
        sprite.filters = [];
      }
      // Clear any pending debounce timeout
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
      // Clear animation
      if (animationRef.current) {
        anime.remove(animationRef.current);
      }
      // Clear restore timeout
      if (restoreTimeoutRef.current) {
        clearTimeout(restoreTimeoutRef.current);
      }
    };
  }, []);

  // Calculate button radius to match the visual sprite exactly - memoized
  const buttonRadius = useMemo(() => buttonConfig.width / 2, [buttonConfig.width]);

  // Calculate button opacity based on game state:
  // - Disabled (spinning, animations, autoplay, free rounds): 0.5 (transparent)
  // - Active state (orange background, "ACTIVE" text): 1 (fully visible) even when disabled
  // - Base game enabled: 1 (fully visible)
  const buttonOpacity = isDisabled && !isButtonActive ? 0.5 : 1;

  return (
    <Container
      name="secondary_button_bonus_buy"
      x={buttonPosition.x}
      y={buttonPosition.y}
      alpha={buttonOpacity}
    >
      {/* Interactive circular area - handles all pointer events */}
      <Graphics
        name="button_bonus_buy_interactive_area"
        eventMode={isDisabled ? "none" : "static"}
        cursor={isDisabled ? "default" : "pointer"}
        hitArea={new PIXI.Circle(0, 0, buttonRadius)}
        onpointertap={handleClick}
        onpointerover={handlePointerOver}
        onpointerout={handlePointerOut}
        draw={(g) => {
          g.clear();
          // Nearly invisible fill (required for hit detection to work)
          g.beginFill(0x000000, 0.01);
          g.drawCircle(0, 0, buttonRadius);
          g.endFill();
        }}
      />

      {/* Visual sprite (non-interactive, just for display) */}
      <Sprite
        ref={buttonSpriteRef}
        texture={texture}
        name="button_bonus_buy_icon"
        anchor={0.5}
        width={buttonConfig.width}
        height={buttonConfig.height}
        x={0}
        y={0}
        alpha={1}
        eventMode="none"
      />
      <Text
        ref={buttonTextRef}
        text={displayText}
        style={textStyle}
        anchor={0.5}
        x={0}
        y={0}
        alpha={1}
        resolution={2}
        eventMode="none"
      />
    </Container>
  );
};

export default ButtonBonusBuy;
